export const DEVICE_PACKAGE_KEYS = ["foundation", "recognition", "insight"] as const;

export type DevicePackageKey = (typeof DEVICE_PACKAGE_KEYS)[number];

export type DevicePackageCapabilityKey =
  | "countMonitoring"
  | "mosquito_count_monitoring"
  | "deviceOverview"
  | "device_overview"
  | "lureReminder"
  | "lure_replacement_reminder"
  | "speciesRecognition"
  | "mosquito_species_recognition"
  | "historyTrend"
  | "history_trends"
  | "dataReport"
  | "data_reports"
  | "trend_analysis"
  | "comparison_analysis"
  | "prediction_analysis"
  | "mosquito_distribution"
  | "reportExport"
  | "report_export";

export interface DevicePackageDefinition {
  key: DevicePackageKey;
  labelI18nKey: string;
  descriptionI18nKey: string;
  capabilityKeys: DevicePackageCapabilityKey[];
  tagColor: string;
}

export const DEVICE_PACKAGE_CAPABILITY_KEYS: DevicePackageCapabilityKey[] = [
  "countMonitoring",
  "mosquito_count_monitoring",
  "deviceOverview",
  "device_overview",
  "lureReminder",
  "lure_replacement_reminder",
  "speciesRecognition",
  "mosquito_species_recognition",
  "historyTrend",
  "history_trends",
  "dataReport",
  "data_reports",
  "trend_analysis",
  "comparison_analysis",
  "prediction_analysis",
  "mosquito_distribution",
  "reportExport",
  "report_export",
];

export const DEVICE_PACKAGE_DEFINITIONS: Record<
  DevicePackageKey,
  DevicePackageDefinition
> = {
  foundation: {
    key: "foundation",
    labelI18nKey: "packages.options.foundation.name",
    descriptionI18nKey: "packages.options.foundation.description",
    capabilityKeys: [
      "countMonitoring",
      "mosquito_count_monitoring",
      "deviceOverview",
      "device_overview",
      "lureReminder",
      "lure_replacement_reminder",
    ],
    tagColor: "default",
  },
  recognition: {
    key: "recognition",
    labelI18nKey: "packages.options.recognition.name",
    descriptionI18nKey: "packages.options.recognition.description",
    capabilityKeys: [
      "countMonitoring",
      "mosquito_count_monitoring",
      "deviceOverview",
      "device_overview",
      "lureReminder",
      "lure_replacement_reminder",
      "speciesRecognition",
      "mosquito_species_recognition",
      "historyTrend",
      "history_trends",
      "mosquito_distribution",
    ],
    tagColor: "blue",
  },
  insight: {
    key: "insight",
    labelI18nKey: "packages.options.insight.name",
    descriptionI18nKey: "packages.options.insight.description",
    capabilityKeys: [
      "countMonitoring",
      "mosquito_count_monitoring",
      "deviceOverview",
      "device_overview",
      "lureReminder",
      "lure_replacement_reminder",
      "speciesRecognition",
      "mosquito_species_recognition",
      "historyTrend",
      "history_trends",
      "trend_analysis",
      "comparison_analysis",
      "prediction_analysis",
      "mosquito_distribution",
      "reportExport",
      "report_export",
    ],
    tagColor: "green",
  },
};

export const isDevicePackageKey = (
  value: string | null | undefined,
): value is DevicePackageKey =>
  DEVICE_PACKAGE_KEYS.includes(value as DevicePackageKey);

export const normalizeDevicePackageKey = (
  value: string | null | undefined,
): DevicePackageKey => (isDevicePackageKey(value) ? value : "foundation");

export const getDevicePackageDefinition = (
  value: string | null | undefined,
): DevicePackageDefinition =>
  DEVICE_PACKAGE_DEFINITIONS[normalizeDevicePackageKey(value)];

export const hasDevicePackageCapability = (
  packageKey: string | null | undefined,
  capabilityKey: DevicePackageCapabilityKey,
): boolean =>
  getDevicePackageDefinition(packageKey).capabilityKeys.includes(capabilityKey);
